import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from "react-router-dom"
import classes from './DocumentReviewAssessment.module.scss'
import PageHeading from '../../../shared/PageHeading'
import useAssessment from '../../../../customHooks/useAssessment'
import ProgressBar from '../ProgressBar'
import Button from '../../../shared/ComponentLibrary/RButton'
import Icon from '../../../../Icon'
import Timer from '../Timer'
import PulseWheel from '../PulseWheel'
import { toast } from 'react-toastify'
import AssessmentFeedback from './DocumentReviewAssessmentFeedback'
import { scrollDocToTop } from '../../../../helpers/methods'
import { datadogLogs } from '@datadog/browser-logs'
import { useSelector } from 'react-redux'

const StartAssessment = () => {
  const navigate = useNavigate()
  const {startAssessment, submitAssessment, cancelAssessment } = useAssessment()
  const [error, setError] = useState('')
  const [status, setStatus] = useState<IStatus>({ status: false })
  const [assessmentData, setAssessmentData] = useState<IAssessmentData>({})
  const [documentNumber, setDocumentNumber] = useState(1)
  const [eligible, setEligible] = useState(false)
  const [resetTimer, setResetTimer] = useState(false)
  const [pulseWheelTimeUp, setPulseWheelTimeUp] = useState(false)
  const { register, reset, handleSubmit } = useForm()
  const scrollRef = useRef<any>()
  const profileStatus: UserProfileStatus = useSelector((state: ProfileState) => state.profile)
  const location = useLocation()
  const [selectedLanguage, setSelectedLanguage] = useState(location.state?.language || 'en')

  useEffect(() => {
    if (profileStatus.profileStatus) {
      startAssessment({language: selectedLanguage, setAssessmentData, setError })
    }
  }, [profileStatus.profileStatus])
  useEffect(() => console.log(error), [error])

  useEffect(() => {
    status.status && navigate('/')
    toast.info(status.message)
  }, [status])

    useEffect(() => {
    if (!profileStatus.profileStatus) {
      navigate('/')
      toast.error('Profile Incomplete!')
    }
  }, [])

  useEffect(() => {
    if (assessmentData.eligible?.status === false) {
      navigate('/')
      toast.info(assessmentData.eligible?.message)
    }
    else {
      setEligible(true)
      assessmentData.assessment?.documentNumber && setDocumentNumber(assessmentData.assessment.documentNumber)
    }
  }, [assessmentData.eligible])

  useEffect(() => console.log(assessmentData), [assessmentData])

  const submitHandler = (data: any) => {
    let successHandler = (submitData: ISubmitResponseData) => {
      setDocumentNumber((current) => current + 1)
      reset()
      datadogLogs.logger.info('Assessment: Document Questions Submitted', {documentNumberInAssessment: documentNumber, details: submitData});
      scrollDocToTop(scrollRef)
      setResetTimer((current) => !current )
    }
    let errorHandler = () => {
      console.log("assessment failed to submit")
    }
    
    if (assessmentData.legalDocument) {
      let submitData: ISubmitResponseData = {
        language: selectedLanguage,
        attributes: {
          legalDocumentId: assessmentData.legalDocument.id,
          response: []
        },
        setAssessmentData: setAssessmentData,
        setError: setError
      }
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          submitData.attributes?.response.push({ questionId: parseInt(key), answer: data[key] === 'true' })
        }
      })
      console.log(submitData)
      
      submitAssessment(submitData, successHandler, errorHandler)
    }
  }

  const readyToShowPages = () => Boolean(assessmentData.legalDocument?.content.length)
  const forceDoNotShowPages = () => Boolean(!readyToShowPages() || pulseWheelTimeUp)

  return (
    <div className={classes.assessment_container}>
      {
        assessmentData?.isCompleted ? 
        <AssessmentFeedback /> :
        eligible &&
        <div>
          <div className={classes.header}>
            <PageHeading title='ReviewRight® Assessment' icon='testResults' />
            { assessmentData.assessment && <Timer seconds={assessmentData.assessment?.secondsRemaining} showIcon design='total'/> }
          </div>

          <ProgressBar currentValue={documentNumber - 1} maxValue={15}/>

          <form onSubmit={handleSubmit(submitHandler)}>
            <div className={`${classes.assessment_section} ${forceDoNotShowPages() ? classes.pulse_wheel_holder : ''}`}>
              <PulseWheel show={!readyToShowPages()} style={{borderColor: "#2CB6D1", borderTopColor: "transparent"}} timer={60} timeUp={pulseWheelTimeUp} setTimeUp={setPulseWheelTimeUp}>
                <div>
                  <p>The connection appears to be unstable. Please try again later.</p>
                  <p>We recommend taking the assessment from a desktop computer with a stable connection.</p>
                </div>
              </PulseWheel>
              <div className={`${classes.title_section} ${forceDoNotShowPages() ? classes.hidden : ''}`}>
                <div className={classes.title}>
                  <b>Document {documentNumber}:</b> "{ assessmentData.legalDocument && assessmentData.legalDocument.title }"
                </div>
                { assessmentData.assessment && <Timer seconds={assessmentData.assessment?.secondsElapsed} incremental resetTimer={resetTimer} design='section' /> }
              </div>
              <hr className={classes.divider}/>

              <div className={`${classes.questions_section} ${forceDoNotShowPages() ? classes.hidden : ''}`}>
                <div className={classes.document_section} ref={scrollRef}>
                  <div className={classes.document}>
                     {assessmentData.legalDocument?.content && (
                       <div className={classes.document_content}>
                         {assessmentData.legalDocument.content}
                       </div>
                     )}
                  </div>
                </div>
                <div className={classes.questions}>
                  {
                    assessmentData.assessment && assessmentData.assessment.questions.map((question) => {
                      return (
                        <div className={classes.question} key={question.id}>
                          <div className={classes.text}>
                            <b>{question.id}. </b>
                            <div>{question.question}</div>
                          </div>
                          <div className={classes.options}>
                            <div>
                              <label>
                                <input
                                  type="radio"
                                  {...register(question.id.toString())}
                                  value="true"
                                  id={`question_${question.id}_true`}
                                  data-dd-privacy="allow"
                                /> Yes
                              </label>
                            </div>
                            <div>
                              <label>
                                <input
                                  type="radio"
                                  {...register(question.id.toString())}
                                  value="false"
                                  id={`question_${question.id}_false`}
                                  data-dd-privacy="allow"
                                /> No
                              </label>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  }
                </div>
              </div>
            </div>
            <div className={classes.buttons}>
              <div className={classes.cancel_button}>
                <Button type='button' variant='outlined' onClickMethod={() => cancelAssessment({setStatus, setError})}>
                  Cancel Assessment
                </Button>  
              </div>
              <div className={classes.submit_button}>
                <Button type='submit' hidden={forceDoNotShowPages()}>
                  Next Document
                  <Icon name='longArrowLeft' color='#FFF' transform />
                </Button>  
              </div>
            </div>
          </form>
        </div>
      }
    </div>
  )
}

export default StartAssessment
